import React, { useState } from 'react';
import { EquipmentRecommendation, ShaftOption } from '../services/recommendationService';

interface RecommendationCardProps {
  recommendation: EquipmentRecommendation;
  onExpand?: (recommendation: EquipmentRecommendation) => void;
  onCompare?: (recommendation: EquipmentRecommendation) => void;
  className?: string;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  onExpand,
  onCompare,
  className = ""
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (onExpand) {
      onExpand(recommendation);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 9.0) return { bg: 'bg-green/15', text: 'text-green-ink', bar: 'bg-green/100', label: 'Excellent' };
    if (score >= 8.0) return { bg: 'bg-green/10', text: 'text-green-ink', bar: 'bg-green/100', label: 'Very Good' };
    if (score >= 7.0) return { bg: 'bg-blue-50', text: 'text-blue-700', bar: 'bg-blue-500', label: 'Good' };
    if (score >= 6.0) return { bg: 'bg-yellow-50', text: 'text-yellow-700', bar: 'bg-yellow-500', label: 'Fair' };
    return { bg: 'bg-gray-50', text: 'text-gray-700', bar: 'bg-gray-400', label: 'Below Average' };
  };

  const renderPerformanceBar = (label: string, rating?: number) => {
    if (!rating) return null;

    const percentage = (rating / 10) * 100;
    const colors = getScoreColor(rating);

    return (
      <div className="mb-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm font-bold text-gray-900">{rating.toFixed(1)}/10</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`${colors.bar} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  const scoreColors = getScoreColor(recommendation.overall_score);
  const rank = recommendation.rank_position;

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer ${className}`}
      onClick={handleToggleExpand}
    >
      {/* Main Card Content */}
      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          {/* Left side - Rank badge and equipment info */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Rank badge */}
            <div className="flex-shrink-0">
              <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm ${
                rank === 1 ? 'bg-yellow-500' :
                rank === 2 ? 'bg-gray-400' :
                rank === 3 ? 'bg-amber-600' : 'bg-gray-300'
              }`}>
                #{rank}
              </div>
            </div>

            {/* Equipment details */}
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">
                {recommendation.manufacturer} {recommendation.model}
                {recommendation.loft != null && (
                  <span className="ml-1.5 text-sm font-normal text-gray-500">{recommendation.loft}°</span>
                )}
              </h3>
              <p className="text-gray-600 mt-1 text-sm">
                {recommendation.detailed_explanation || recommendation.fit_reason}
              </p>

              {/* Stock shaft guide */}
              {recommendation.stock_shaft_recommendation && (
                <div className="mt-3 bg-green/10 border border-green-ink rounded-md px-3 py-2">
                  <span className="text-xs font-semibold text-green-ink uppercase tracking-wide">Stock Shaft Guide</span>
                  <p className="text-sm text-green-ink mt-0.5">{recommendation.stock_shaft_recommendation}</p>
                </div>
              )}

              {/* Shaft options */}
              {recommendation.top_shafts && recommendation.top_shafts.length > 0 && (
                <div className="mt-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Top Shaft Options</span>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {recommendation.top_shafts.map((shaft: ShaftOption, i: number) => (
                      <div
                        key={i}
                        className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs border ${
                          i === 0
                            ? 'bg-green/10 border-green-ink text-green-ink'
                            : 'bg-gray-50 border-gray-200 text-gray-700'
                        }`}
                        title={`${shaft.shaft_brand} ${shaft.shaft_model}`}
                      >
                        {i === 0 && (
                          <svg className="w-3.5 h-3.5 text-green-ink mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                        <span className="font-semibold">{shaft.shaft_brand} {shaft.shaft_model}</span>
                        <span className="mx-1.5 text-gray-300">|</span>
                        <span>{shaft.weight}g · {shaft.flex}</span>
                        {shaft.shaft_score != null && (
                          <>
                            <span className="mx-1.5 text-gray-300">|</span>
                            <span className="font-semibold">{shaft.shaft_score.toFixed(1)}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Show Details link */}
              <button
                onClick={(e) => { e.stopPropagation(); handleToggleExpand(); }}
                className="mt-3 text-sm text-green-ink hover:text-green-ink font-medium flex items-center"
              >
                <svg
                  className={`w-4 h-4 mr-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {isExpanded ? 'Hide Details' : 'Show Details'}
              </button>
            </div>
          </div>

          {/* Right side - Score badge */}
          <div className="flex-shrink-0 text-right" onClick={(e) => e.stopPropagation()}>
            <div className="text-2xl sm:text-3xl font-bold text-green-ink mb-0.5 leading-none">
              {recommendation.overall_score.toFixed(1)}
            </div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">/10</div>
            <div className={`mt-1.5 ${scoreColors.bg} ${scoreColors.text} px-2 py-0.5 rounded-full text-xs font-medium text-center whitespace-nowrap`}>
              {scoreColors.label}
            </div>
            {recommendation.price != null && recommendation.price > 0 && (
              <div className="mt-1.5 text-sm text-gray-500">
                ${recommendation.price.toFixed(0)}
              </div>
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            {/* Performance Metrics */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-green-ink mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Performance Metrics
              </h4>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {renderPerformanceBar('Goal Fit', recommendation.distance_rating)}
                {renderPerformanceBar('Forgiveness', recommendation.forgiveness_rating)}
                {renderPerformanceBar('Weight Fit', recommendation.workability_rating)}
                {renderPerformanceBar('Flex Match', recommendation.feel_rating)}
              </div>
            </div>

            {/* Key Features */}
            {recommendation.key_features && recommendation.key_features.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 text-green-ink mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Key Features
                </h4>

                <div className="space-y-2">
                  {recommendation.key_features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <svg className="w-4 h-4 text-green-ink mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Technical Specifications */}
            {recommendation.technology_highlights && recommendation.technology_highlights.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 text-green-ink mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                  Technical Specifications
                </h4>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {recommendation.technology_highlights.map((tech, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium text-gray-900">{tech}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationCard;